/**
 * Agent-side helpers: an agent principal signs its *own* requests with its own key and
 * carries the human's Pryme-signed mandate. No human private key is ever involved.
 */
import { HEADERS, REQUEST_MAX_AGE_SECONDS, TYP } from '@pryme/protocol';
import { contentDigest, ed25519, isoSeconds, jwsDecode, jwsSign, randomB64, signatureBase, utf8 } from './crypto.js';
import { PrymeSdkError } from './errors.js';
import { publicJwk } from './ap2.js';
import { encodeAgentSecret, isSecretKey, parseAgentSecret } from './secret.js';
/** Resolve an `AgentConfig` to explicit credentials (raw key). Explicit fields override the secret's. */
export function resolveAgentConfig(input) {
    const bad = (m) => {
        throw new PrymeSdkError('invalid_request', `Pryme agent configuration: ${m}`);
    };
    const o = (typeof input === 'string' ? { secretKey: input } : input);
    if (typeof o?.secretKey !== 'string' || !o.secretKey)
        return bad('secretKey (PRYME_AGENT_SECRET_KEY) is required');
    if (!isSecretKey(o.secretKey)) {
        // Explicit configuration with a raw Ed25519 secret.
        for (const f of ['agentId', 'keyId', 'apiBase', 'audience'])
            if (!o[f])
                bad(`${f} is required with a raw secretKey (or pass the sk_test_… / sk_live_… agent secret key)`);
        return o;
    }
    let a;
    try {
        a = parseAgentSecret(o.secretKey);
    }
    catch (e) {
        return bad(e.message);
    }
    if (o.agentId && o.agentId !== a.agentId)
        bad(`secretKey belongs to ${a.agentId}, not ${o.agentId}`);
    if (o.keyId && o.keyId !== a.keyId)
        bad(`secretKey is for key ${a.keyId}, not ${o.keyId}`);
    return { agentId: a.agentId, keyId: a.keyId, secretKey: a.secretKey, apiBase: o.apiBase ?? a.apiBase, audience: o.audience ?? a.audience };
}
/**
 * Connect an agent to a human without copying credentials (RFC 8628 style). The agent generates its
 * own Ed25519 key; show `userCode`/`verificationUriComplete` to the human, who approves in Pryme
 * with a passkey. `credentials()` resolves with the agent secret key (`secretKey`, `sk_test_…` /
 * `sk_live_…`), assembled here from the approval and the local key: store it in the agent's secret
 * store as `PRYME_AGENT_SECRET_KEY` and pass it to `createAgent`. The private key never leaves the agent.
 */
export async function pairAgent(args) {
    const doFetch = args.fetch ?? ((...a) => fetch(...a));
    const key = ed25519.generate();
    const r = await doFetch(new URL('/v1/agents/pairings', args.apiBase).toString(), {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name: args.name, publicKey: key.publicKey, webhookUrl: args.webhookUrl }),
    });
    const j = (await r.json().catch(() => ({})));
    if (r.status !== 201)
        throw new PrymeSdkError(j.error?.code ?? 'temporarily_unavailable', j.error?.message);
    const pairing = j;
    return {
        ...pairing,
        secretKey: key.secretKey,
        /**
         * Poll until the human decides. Resolves with the agent secret key (`secretKey`), the public
         * agent id, key id, API base, audience and environment, (once) the webhook secret, and
         * `approvedBy.handle`: the Pryme handle of the account that approved (null if it has none). Check
         * it is the account you meant: whoever enters a leaked code first owns the pairing.
         */
        async credentials(opts = {}) {
            const until = Date.now() + (opts.timeoutMs ?? pairing.expiresIn * 1000);
            for (;;) {
                const s = await pollPairing({ apiBase: args.apiBase, pairingId: pairing.pairingId, secretKey: key.secretKey, fetch: doFetch });
                if (s.error)
                    throw new PrymeSdkError(s.error.code ?? 'temporarily_unavailable', s.error.message);
                if (s.status === 'approved') {
                    const environment = s.environment === 'production' ? 'production' : 'sandbox';
                    const secretKey = encodeAgentSecret({ agentId: s.agentId, keyId: s.keyId, secretKey: key.secretKey, apiBase: s.apiBase, audience: s.audience, environment });
                    return { agentId: s.agentId, keyId: s.keyId, secretKey, apiBase: s.apiBase, audience: s.audience, environment, webhookSecret: s.webhookSecret, approvedBy: { handle: s.approvedBy?.handle ?? null } };
                }
                if (s.status !== 'pending')
                    throw new PrymeSdkError(s.status === 'denied' ? 'approval_denied' : 'expired_presentation', `pairing ${s.status}`);
                if (Date.now() > until)
                    throw new PrymeSdkError('expired_presentation', 'pairing timed out');
                await new Promise((res) => setTimeout(res, pairing.interval * 1000));
            }
        },
    };
}
/**
 * One pairing status poll, signed with the key being paired (a leaked pairing id alone reveals
 * nothing). `{status: pending|approved|denied|expired}`; approved adds agentId, keyId, apiBase,
 * audience, environment and — exactly once — webhookSecret.
 */
export async function pollPairing(args) {
    const doFetch = args.fetch ?? ((...a) => fetch(...a));
    const url = new URL(`/v1/agents/pairings/${encodeURIComponent(args.pairingId)}`, args.apiBase);
    const nonce = randomB64(18);
    const timestamp = isoSeconds();
    const digest = contentDigest('');
    const base = signatureBase({
        principal: `agent-pairing:${args.pairingId}`, keyId: 'pairing', method: 'GET', audience: 'pryme-agent-pairing',
        target: url.pathname + url.search, contentDigest: digest, timestamp, nonce,
    });
    const r = await doFetch(url.toString(), { headers: {
            [HEADERS.keyId]: 'pairing', [HEADERS.timestamp]: timestamp, [HEADERS.nonce]: nonce, [HEADERS.digest]: digest,
            [HEADERS.signature]: `ed25519=${ed25519.sign(args.secretKey, utf8(base))}`,
        } });
    return (await r.json().catch(() => ({ error: { code: 'temporarily_unavailable' } })));
}
/** Verify a Pryme webhook (Standard Webhooks v1). Returns the parsed event or throws. */
export async function verifyWebhook(secret, headers, body, opts = {}) {
    const get = (k) => (headers instanceof Headers ? headers.get(k) : headers[k]) ?? undefined;
    const id = get('webhook-id');
    const ts = get('webhook-timestamp');
    const sig = get('webhook-signature');
    if (!id || !ts || !sig)
        throw new PrymeSdkError('invalid_signature', 'missing webhook headers');
    if (Math.abs(Date.now() / 1000 - Number(ts)) > (opts.toleranceSeconds ?? 300))
        throw new PrymeSdkError('expired_presentation', 'webhook timestamp outside tolerance');
    const raw = Uint8Array.from(atob(secret.replace(/^whsec_/, '')), (ch) => ch.charCodeAt(0));
    const k = await crypto.subtle.importKey('raw', raw, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const mac = new Uint8Array(await crypto.subtle.sign('HMAC', k, new TextEncoder().encode(`${id}.${ts}.${body}`)));
    const expected = `v1,${btoa(String.fromCharCode(...mac))}`;
    const ok = sig.split(' ').some((s) => s.length === expected.length && [...s].reduce((acc, ch, i) => acc | (ch.charCodeAt(0) ^ expected.charCodeAt(i)), 0) === 0);
    if (!ok)
        throw new PrymeSdkError('invalid_signature', 'webhook signature mismatch');
    return JSON.parse(body);
}
export function createAgent(config, opts = {}) {
    const creds = resolveAgentConfig(config);
    const doFetch = opts.fetch ?? ((...a) => fetch(...a));
    function signHeaders(method, url, body, audience) {
        const nonce = randomB64(18);
        const timestamp = isoSeconds();
        const digest = contentDigest(body);
        const base = signatureBase({
            principal: `agent:${creds.agentId}`, keyId: creds.keyId, method, audience,
            target: url.pathname + url.search, contentDigest: digest, timestamp, nonce,
        });
        return {
            nonce,
            headers: {
                [HEADERS.agent]: creds.agentId,
                [HEADERS.keyId]: creds.keyId,
                [HEADERS.audience]: audience,
                [HEADERS.timestamp]: timestamp,
                [HEADERS.nonce]: nonce,
                [HEADERS.digest]: digest,
                [HEADERS.signature]: `ed25519=${ed25519.sign(creds.secretKey, utf8(base))}`,
            },
        };
    }
    /** Sign `operation` for `recipient` under the mandate in `proof`, bound to one request nonce. */
    function signIntent(args) {
        const nonce = args.nonce ?? randomB64(18);
        let mandateId;
        try {
            mandateId = jwsDecode(args.proof.mandate).payload.mandate_id;
        }
        catch {
            throw new PrymeSdkError('invalid_request', 'proof.mandate is not a Pryme mandate');
        }
        const operation = { action: args.operation.action };
        for (const k of ['amount', 'currency', 'resource', 'merchant', 'orderId']) {
            if (args.operation[k] !== undefined)
                operation[k] = args.operation[k];
        }
        const iat = Math.floor(Date.now() / 1000);
        const payload = { v: 1, agent_id: creds.agentId, aud: args.recipient, mandate_id: mandateId, operation, nonce, iat, exp: iat + REQUEST_MAX_AGE_SECONDS };
        return { intent: jwsSign(creds.secretKey, creds.keyId, TYP.intent, payload), nonce };
    }
    async function call(method, path, payload) {
        const url = new URL(path, creds.apiBase);
        const body = payload === undefined ? '' : JSON.stringify(payload);
        const { headers } = signHeaders(method, url, body, creds.audience);
        if (body)
            headers['content-type'] = 'application/json';
        const r = await doFetch(url.toString(), { method, headers, body: body || undefined });
        const j = (await r.json().catch(() => ({})));
        if (!r.ok)
            throw new PrymeSdkError(j.error?.code ?? 'temporarily_unavailable', j.error?.message);
        return j;
    }
    return {
        agentId: creds.agentId,
        publicKey: ed25519.publicFromSecret(creds.secretKey),
        /** Active, human-signed mandate proofs plus the status of revoked/expired/suspended ones. */
        async mandateStatus() {
            const url = new URL('/v1/agents/self/mandates', creds.apiBase);
            const { headers } = signHeaders('GET', url, '', creds.audience);
            const r = await doFetch(url.toString(), { headers });
            const j = (await r.json());
            if (!r.ok)
                throw new PrymeSdkError(j.error?.code ?? 'temporarily_unavailable', j.error?.message);
            return { mandates: j.mandates ?? [], inactive: j.inactive ?? [] };
        },
        /** Fetch the human-signed mandate proofs currently granted to this agent. */
        async mandates() {
            return (await this.mandateStatus()).mandates;
        },
        /**
         * Get this agent's delegation proof for a service.
         *  - It already has a permission there → `{ status: 'connected', ...proof }`.
         *  - First contact → pass `request` (the concrete first action, exactly as the service will
         *    check it). Pryme asks the human and returns `{ status: 'approval_required', approvalId }`;
         *    the service learns nothing about the human until they approve. Then `waitForApproval()`.
         */
        async connect(recipient, opts = {}) {
            const j = await call('POST', '/v1/agents/self/connect', { recipient, request: opts.request, actions: opts.actions });
            return j;
        },
        /**
         * Wait for the human's answer to an approval (first contact or an escalated action). Long-polls
         * Pryme (no public URL needed); resolves when decided or after `timeoutMs` (still `pending`).
         * Approved → includes the delegation proof to continue with.
         */
        async waitForApproval(approvalId, opts = {}) {
            const until = Date.now() + (opts.timeoutMs ?? 120_000);
            for (;;) {
                const left = Math.max(0, Math.min(25, Math.floor((until - Date.now()) / 1000)));
                const s = (await call("GET", `/v1/agents/self/approvals/${encodeURIComponent(approvalId)}?wait=${left}`));
                if (s.status !== 'pending' || Date.now() >= until)
                    return s;
            }
        },
        /** Receive `approval.decided` events at `url` (Standard Webhooks signatures). Returns the secret once. */
        async setWebhook(url) {
            return (await call("PUT", "/v1/agents/self/webhook", { url }));
        },
        /**
         * AP2: get Pryme-issued AP2 open checkout + payment mandates for one purchase, bound to
         * `holderKey` (P-256; a fresh key per purchase is fine).
         *  - `checkoutJwt`: the merchant already signed a UCP checkout → mandates for exactly that checkout and amount.
         *  - `intent`: AP2's native order → items and a price cap now; the merchant builds a fitting checkout afterwards.
         * `mandateId` is optional: without it Pryme uses (or creates, ask-first) this agent's permission at the merchant.
         * On `approval_required`, the human approves in the portal; call again with the same input and `approvalId`.
         * Denials throw `PrymeSdkError`.
         */
        async ap2CheckoutMandate(args) {
            const url = new URL('/v1/ap2/checkout-mandates', creds.apiBase);
            const body = JSON.stringify({ mandateId: args.mandateId, checkoutJwt: args.checkoutJwt, intent: args.intent, holderKey: publicJwk(args.holderKey), approvalId: args.approvalId });
            const { headers } = signHeaders('POST', url, body, creds.audience);
            headers['content-type'] = 'application/json';
            const r = await doFetch(url.toString(), { method: 'POST', headers, body });
            const j = (await r.json().catch(() => ({})));
            if (r.status === 201) {
                return {
                    status: 'issued', checkoutMandate: j.checkoutMandate, paymentMandate: j.paymentMandate, mandate: j.checkoutMandate, openCheckoutHash: j.openCheckoutHash,
                    merchant: j.merchant, authorizationId: j.authorizationId, expiresAt: j.expiresAt, issuer: j.issuer, mandateId: j.mandateId,
                    idempotent: j.idempotent === true,
                };
            }
            if (r.status === 202)
                return { status: 'approval_required', approvalId: j.approval?.id, expiresAt: j.approval?.expiresAt, reason: j.reason, mandateId: j.mandateId };
            throw new PrymeSdkError(j.error?.code ?? 'temporarily_unavailable', j.error?.message);
        },
        /**
         * Sign the exact operation you ask `recipient` to perform (`pryme-intent-v1`), for transports other
         * than `request()`. Send it as the `Pryme-Intent` header with the same `nonce` as the request.
         */
        signIntent,
        /**
         * Call a recipient service with a signed request bound to method, path, body, audience and nonce.
         * `intent` is the exact operation you ask for, as the service will check it: signed, it lets the
         * service use your permission for exactly that, once. Without it, the service can only ask the
         * human. Without `proof`, the agent's existing permission for `recipient` is used; on first contact
         * use `connect(recipient, { request })` + `waitForApproval()` first (throws `consent_required` otherwise).
         */
        async request(url, init) {
            let proof = init.proof;
            if (!proof) {
                const c = await this.connect(init.recipient);
                if (c.status !== 'connected')
                    throw new PrymeSdkError('consent_required', 'first contact: call connect(recipient, { request }) and wait for the human');
                proof = c;
            }
            const u = new URL(url);
            const method = (init.method ?? 'POST').toUpperCase();
            const body = init.body === undefined ? '' : JSON.stringify(init.body);
            const { headers, nonce } = signHeaders(method, u, body, init.recipient);
            headers[HEADERS.mandate] = proof.mandate;
            headers[HEADERS.binding] = proof.binding;
            if (init.intent)
                headers[HEADERS.intent] = signIntent({ recipient: init.recipient, proof, operation: init.intent, nonce }).intent;
            if (init.approvalId)
                headers[HEADERS.approval] = init.approvalId;
            if (body)
                headers['content-type'] = 'application/json';
            const res = await doFetch(u.toString(), { method, headers, body: method === 'GET' ? undefined : body });
            return { res, nonce, headers };
        },
    };
}
